import React, { memo, Suspense, useState } from 'react';
import { FiX, FiSquare, FiCheckSquare } from 'react-icons/fi';
import { loadWidget } from '../utils/widgetLoader';
import { createDefaultWidgetData } from '../utils/widgetDefaults';

// Widget Preview Component
const WidgetPreview = memo(function WidgetPreview({ 
  widgetType
}: { 
  widgetType: string;
}) {
  try {
    const defaultData = createDefaultWidgetData(widgetType);
    const LazyComponent = loadWidget(widgetType as any);
    
    return (
      <div className="w-full bg-gray-50 rounded border overflow-hidden relative">
        {/* Full component display without height restrictions */}
        <div className="w-full pointer-events-none select-none">
          <Suspense fallback={
            <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-2 text-sm text-gray-600">Loading...</span>
            </div>
          }>
            <div className="w-full bg-white">
              <LazyComponent
                data={defaultData as any}
                isEditing={false}
                onUpdate={() => {}}
                onNavClick={() => {}}
                onLogoClick={() => {}}
                styles={{ 
                  wrapper: { 
                    minHeight: "auto"
                  } 
                }}
              />
            </div>
          </Suspense>
        </div>
      </div>
    );
  } catch (error) {
    console.error(`WidgetPreview: Error rendering ${widgetType}:`, error);
    return (
      <div className="w-full h-32 bg-red-50 rounded border border-red-200 flex items-center justify-center">
        <div className="text-center">
          <span className="text-red-500 text-sm block">Preview error</span>
          <span className="text-red-400 text-xs">{widgetType}</span>
        </div>
      </div>
    );
  }
});

// Dynamic widget categories function - Only show 4 layout widgets
const createWidgetCategories = (availableCategories?: string[]) => {
  // Fixed list of 4 layout widgets
  const allCategories = {
    "Layout Sections": ['LeftContentRightMediaLayout', 'RightContentLeftMediaLayout', 'SplitPromoCard', 'BasicSocialFollow']
  };

  // If specific categories are requested, filter to only those
  if (availableCategories && availableCategories.length > 0) {
    const filteredCategories: { [key: string]: string[] } = {};
    availableCategories.forEach(category => {
      if (allCategories[category as keyof typeof allCategories]) {
        filteredCategories[category] = allCategories[category as keyof typeof allCategories];
      }
    });
    return filteredCategories;
  }

  return allCategories;
};

export interface WidgetSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWidget: (widgetType: string) => void;
  title?: string;
  categories?: string[];
  maxWidth?: string;
  maxHeight?: string;
}

const WidgetSelectionModal = memo(function WidgetSelectionModal({
  isOpen,
  onClose,
  onSelectWidget,
  title = "Choose Widgets",
  categories,
  maxWidth = "max-w-[90vw]",
  maxHeight = "max-h-[90vh]",
}: WidgetSelectionModalProps) {
  const [selectedWidgets, setSelectedWidgets] = useState<string[]>([]);

  if (!isOpen) return null;

  const widgetCategories = createWidgetCategories(categories);

  const toggleWidget = (widgetType: string) => {
    setSelectedWidgets(prev => 
      prev.includes(widgetType)
        ? prev.filter(w => w !== widgetType)
        : [...prev, widgetType]
    );
  };

  const handleSave = () => {
    if (selectedWidgets.length > 0) {
      // Call onSelectWidget for each selected widget
      selectedWidgets.forEach(widget => onSelectWidget(widget));
      setSelectedWidgets([]);
    }
  };

  const handleCancel = () => {
    setSelectedWidgets([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${maxWidth} w-full ${maxHeight} overflow-hidden flex flex-col`}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600">Choose a layout section to add to your page.</p>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {Object.entries(widgetCategories).map(([category, widgets]) => (
            <div key={category}>
              <div className="grid grid-cols-1 gap-6">
                {widgets.map((widgetType: string) => (
                  <div
                    key={widgetType}
                    className="p-3 border border-gray-200 rounded-lg group"
                  >
                    {/* Checkbox only - no widget name */}
                    <div 
                      className="mb-3 flex items-center gap-3 cursor-pointer"
                      onClick={() => toggleWidget(widgetType)}
                    >
                      {selectedWidgets.includes(widgetType) ? (
                        <FiCheckSquare className="w-6 h-6 flex-shrink-0" style={{ color: '#EF4544' }} />
                      ) : (
                        <FiSquare className="w-6 h-6 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* Widget Preview */}
                    <div>
                      <WidgetPreview widgetType={widgetType} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selectedWidgets.length === 0}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              selectedWidgets.length > 0
                ? 'text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            style={selectedWidgets.length > 0 ? { backgroundColor: '#EE4446' } : undefined}
          >
            Save{selectedWidgets.length > 0 ? ` (${selectedWidgets.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
});

WidgetSelectionModal.displayName = 'WidgetSelectionModal';

export default WidgetSelectionModal;
